import classNames from 'classnames';
import { JSX } from 'react';
import { FormattedMessage } from 'react-intl';

import { useSubscription } from '../../hooks/useSubscription';

import { useSubscriptions } from '../../contexts/SiteContext';
import BasicButton from '../buttons/BasicButton';
import SubscriptionForm from '../forms/SubscriptionForm';
import AuthorImage from '../partials/AuthorImage';
import Background from '../partials/Background';
import HeaderDescription from '../typography/HeaderDescription';
import HeaderTitle from '../typography/HeaderTitle';

import styles from '../../styles/headers/author-default-header.module.css';

export interface AuthorDefaultHeaderProps extends Partial<Author> {
    background?: Background | null;
    color?: string | null;
    placeholder?: boolean;
    className?: string | null;
}

const linkLabels: Record<AuthorLinkType, JSX.Element> = {
    email: <FormattedMessage defaultMessage="Email" description="Link label" />,
    website: <FormattedMessage defaultMessage="Site web" description="Link label" />,
    facebook: <FormattedMessage defaultMessage="Facebook" description="Link label" />,
    twitter: <FormattedMessage defaultMessage="Twitter" description="Link label" />,
    youtube: <FormattedMessage defaultMessage="YouTube" description="Link label" />,
    tiktok: <FormattedMessage defaultMessage="TikTok" description="Link label" />,
    linkedin: <FormattedMessage defaultMessage="LinkedIn" description="Link label" />,
    instagram: <FormattedMessage defaultMessage="Instagram" description="Link label" />,
};

const AuthorDefaultHeader = ({
    id = null,
    color = null,
    background = null,
    slug = null,
    description = null,
    image = null,
    name = null,
    title = null,
    email = null,
    links = null,
    placeholder = false,
    className = null,
}: AuthorDefaultHeaderProps) => {
    const subscriptions = useSubscriptions();
    const subscription =
        subscriptions.find(({ author = null }) => author !== null && author.id === id) || null;
    const { subscribed = false, unsubscribe } = useSubscription({
        subscription: subscription !== null ? subscription.handle : null,
    });

    const finalLinks = [
        // ...(email !== null
        //     ? [
        //           {
        //               type: 'email' as AuthorLinkType,
        //               url: `mailto:${email}`,
        //           },
        //       ]
        //     : []),
        ...(links !== null ? links : []),
    ];

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.withoutImage]: image === null && !placeholder,
                },
                className,
            ])}
            style={{
                color,
            }}
        >
            {background !== null ? (
                <Background {...background} className={styles.background} />
            ) : null}

            <div className={styles.inner}>
                {image !== null || placeholder ? (
                    <div className={styles.imageContainer}>
                        <AuthorImage
                            name={name}
                            slug={slug}
                            media={image}
                            size="responsive"
                            className={styles.image}
                            withPlaceholder={placeholder}
                            withoutCircle
                            pictureClassName={styles.picture}
                            initialsClassName={styles.initials}
                        />
                    </div>
                ) : null}
                <div className={styles.label}>
                    <HeaderTitle className={styles.name} placeholder={placeholder}>
                        {name}

                        {subscription !== null && subscribed !== null ? (
                            <div className={styles.subscription}>
                                {!subscribed ? (
                                    <SubscriptionForm
                                        subscription={subscription.handle}
                                        source="author_page"
                                        buttonOnly
                                        withoutSso
                                        className={styles.form}
                                    />
                                ) : (
                                    <div>
                                        Vous êtes abonné·e! (
                                        <BasicButton
                                            onClick={() => unsubscribe()}
                                            className={styles.link}
                                        >
                                            Se désabonner
                                        </BasicButton>
                                        )
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </HeaderTitle>
                    {title !== null ? (
                        <HeaderDescription html={title} className={styles.roles} />
                    ) : null}
                    {description !== null ? (
                        <HeaderDescription html={description} className={styles.description} />
                    ) : null}
                    {email !== null ? (
                        <div className={styles.email}>
                            <FormattedMessage defaultMessage="Courriel :" description="Label" />{' '}
                            <a href={`mailto:${email}`}>{email}</a>
                        </div>
                    ) : null}
                    {finalLinks.length > 0 ? (
                        <div className={styles.links}>
                            <ul className={styles.items}>
                                {finalLinks.map(({ type, url }) => (
                                    <li key={type} className={styles.item}>
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.link}
                                        >
                                            {linkLabels[type]}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default AuthorDefaultHeader;
